import React from "react";
import { View, Pressable } from "react-native-web";
import TextField from "@mui/material/TextField";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

import CustomText from "../../atoms/CustomText";
import { colors, spacing } from "../../../utils/styling";
import { Exercise } from "../../../utils/category_mappings";
import {
  parseMapItems,
  parseActionPlanFields,
  parseTrackerFields,
  parseRankingOptions,
  TRACKER_DAYS,
} from "../../../lib/exercisePrompts";

// Interactive bodies for exercise types 2–5 of the per-personality 5-Step
// Guided Action Plan (ClickUp 86badfk6e). Each is a controlled component:
// `value` is the saved `response` jsonb for that exercise, `onChange` emits the
// next value. The parent (GuidedActionPlan) owns the save/persist lifecycle and
// derives `completed` from `isExerciseComplete` below. The backend table is
// schemaless (response jsonb), so every type reuses the same /exercises API.

// ---------------------------------------------------------------------------
// Shared response shapes (also documented in money-exercise-responses.sql)
// ---------------------------------------------------------------------------
export interface MapResponse {
  // placements the user has labeled with their "why"
  placements: { label: string; note: string }[];
}
export interface ActionPlanResponse {
  fields: Record<string, string>; // label-key -> value
}
export interface TrackerResponse {
  days: { date: string; entries: Record<string, string> }[];
}
export interface RankingResponse {
  ranking: string[]; // ordered best→least
}

// ---------------------------------------------------------------------------
// Exercise 2 — Visualization / mapping: place-and-label board
// ---------------------------------------------------------------------------
// Suggested items (parsed from the prompt) sit as chips. Tapping a chip places
// it on the board, where the user writes their "why". Users can also add their
// own items, so personalities whose prompt isn't a simple list (grid / radar /
// pyramid / collage / a content-bug duplicate) still get a working board.

export function MapBody({
  exercise,
  value,
  onChange,
}: {
  exercise: Exercise;
  value: MapResponse;
  onChange: (v: MapResponse) => void;
}) {
  const placements = value.placements || [];
  const placedLabels = new Set(placements.map((p) => p.label.toLowerCase()));
  const suggestions = React.useMemo(
    () => parseMapItems(exercise.prompt).filter((s) => !placedLabels.has(s.toLowerCase())),
    [exercise.prompt, placements] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [customLabel, setCustomLabel] = React.useState("");

  const place = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    if (placements.some((p) => p.label.toLowerCase() === trimmed.toLowerCase())) return;
    onChange({ placements: [...placements, { label: trimmed, note: "" }] });
  };
  const updateNote = (idx: number, note: string) => {
    const next = placements.slice();
    next[idx] = { ...next[idx], note };
    onChange({ placements: next });
  };
  const remove = (idx: number) => {
    onChange({ placements: placements.filter((_, i) => i !== idx) });
  };

  return (
    <View style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
      {/* Suggested chips */}
      {suggestions.length > 0 && (
        <View>
          <BodyLabel>Tap to add to your board</BodyLabel>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: spacing.sm,
            }}
          >
            {suggestions.map((s) => (
              <Pressable
                key={s}
                onPress={() => place(s)}
                style={{
                  backgroundColor: "#EEF2F6",
                  border: "2px solid #C8D3DD",
                  borderRadius: 9999,
                  paddingVertical: spacing.xs,
                  paddingHorizontal: spacing.md,
                }}
              >
                <CustomText style={{ fontFamily: "Nunito", fontSize: 14, color: colors.dark }}>
                  + {s}
                </CustomText>
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {/* Add-your-own */}
      <View style={{ display: "flex", flexDirection: "row", gap: spacing.sm, alignItems: "center" }}>
        <TextField
          value={customLabel}
          onChange={(e) => setCustomLabel(e.target.value)}
          placeholder="Add your own…"
          size="small"
          variant="outlined"
          style={{ flex: 1 }}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              place(customLabel);
              setCustomLabel("");
            }
          }}
        />
        <Pressable
          onPress={() => {
            place(customLabel);
            setCustomLabel("");
          }}
          style={{
            backgroundColor: colors.primary,
            borderRadius: 8,
            paddingVertical: spacing.sm,
            paddingHorizontal: spacing.md,
          }}
        >
          <CustomText style={{ fontFamily: "Nunito", color: colors.white, fontWeight: 600 }}>
            Add
          </CustomText>
        </Pressable>
      </View>

      {/* The board: placed items, each with a "why" note */}
      <View
        style={{
          backgroundColor: "#F7FAFC",
          border: "2px dashed #B6C4D0",
          borderRadius: 12,
          padding: spacing.md,
          minHeight: 120,
          display: "flex",
          flexDirection: "column",
          gap: spacing.sm,
        }}
      >
        {placements.length === 0 ? (
          <CustomText
            style={{
              fontFamily: "Nunito",
              fontSize: 14,
              color: colors.gray[500],
              textAlign: "center",
              marginTop: spacing.md,
            }}
          >
            Your board is empty — add items above, then label each with your why.
          </CustomText>
        ) : (
          placements.map((p, idx) => (
            <View
              key={`${p.label}-${idx}`}
              style={{
                backgroundColor: colors.white,
                border: "2px solid #DCE4EB",
                borderRadius: 10,
                padding: spacing.sm,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: spacing.sm,
              }}
            >
              <CustomText
                style={{
                  fontFamily: "Nunito",
                  fontSize: 15,
                  fontWeight: 700,
                  color: colors.dark,
                  minWidth: 110,
                }}
              >
                {p.label}
              </CustomText>
              <TextField
                value={p.note}
                onChange={(e) => updateNote(idx, e.target.value)}
                placeholder="…my why / what it represents"
                size="small"
                variant="outlined"
                style={{ flex: 1 }}
              />
              <Pressable onPress={() => remove(idx)} style={{ padding: spacing.xs }}>
                <CustomText style={{ fontFamily: "Nunito", color: colors.danger, fontSize: 18 }}>
                  ×
                </CustomText>
              </Pressable>
            </View>
          ))
        )}
      </View>
    </View>
  );
}

export function isMapComplete(v: MapResponse): boolean {
  return !!v.placements && v.placements.some((p) => p.note.trim().length > 0);
}

// ---------------------------------------------------------------------------
// Exercise 3 — Action planning: structured form
// ---------------------------------------------------------------------------

export function ActionPlanBody({
  exercise,
  value,
  onChange,
}: {
  exercise: Exercise;
  value: ActionPlanResponse;
  onChange: (v: ActionPlanResponse) => void;
}) {
  const fields = React.useMemo(
    () => parseActionPlanFields(exercise.prompt),
    [exercise.prompt]
  );
  const values = value.fields || {};
  const set = (key: string, v: string) => onChange({ fields: { ...values, [key]: v } });

  return (
    <View style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
      {fields.map((f) => (
        <View key={f.key}>
          <BodyLabel>{f.label}</BodyLabel>
          {f.isDate ? (
            <TextField
              type="date"
              value={values[f.key] || ""}
              onChange={(e) => set(f.key, e.target.value)}
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <TextField
              value={values[f.key] || ""}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder="Your answer…"
              fullWidth
              multiline
              minRows={1}
              variant="outlined"
            />
          )}
        </View>
      ))}
    </View>
  );
}

export function isActionPlanComplete(v: ActionPlanResponse): boolean {
  const vals = v.fields || {};
  return Object.values(vals).some((x) => (x || "").trim().length > 0);
}

// ---------------------------------------------------------------------------
// Exercise 4 — Behavior pattern tracker: ~3-day daily log
// ---------------------------------------------------------------------------

export function TrackerBody({
  exercise,
  value,
  onChange,
}: {
  exercise: Exercise;
  value: TrackerResponse;
  onChange: (v: TrackerResponse) => void;
}) {
  const fields = React.useMemo(
    () => parseTrackerFields(exercise.prompt),
    [exercise.prompt]
  );

  // Ensure exactly TRACKER_DAYS day slots exist.
  const days =
    value.days && value.days.length === TRACKER_DAYS
      ? value.days
      : Array.from({ length: TRACKER_DAYS }, (_, i) => value.days?.[i] || { date: "", entries: {} });

  const setDay = (
    dayIdx: number,
    patch: Partial<{ date: string; entries: Record<string, string> }>
  ) => {
    const next = days.map((d, i) => (i === dayIdx ? { ...d, ...patch } : d));
    onChange({ days: next });
  };
  const setEntry = (dayIdx: number, key: string, v: string) => {
    setDay(dayIdx, { entries: { ...(days[dayIdx].entries || {}), [key]: v } });
  };

  return (
    <View style={{ display: "flex", flexDirection: "column", gap: spacing.lg }}>
      {days.map((day, dayIdx) => (
        <View
          key={dayIdx}
          style={{
            border: "2px solid #DCE4EB",
            borderRadius: 12,
            padding: spacing.md,
            display: "flex",
            flexDirection: "column",
            gap: spacing.sm,
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <CustomText
              style={{ fontFamily: "Nunito", fontSize: 18, fontWeight: 700, color: colors.dark }}
            >
              Day {dayIdx + 1}
            </CustomText>
            <TextField
              type="date"
              value={day.date || ""}
              onChange={(e) => setDay(dayIdx, { date: e.target.value })}
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </View>
          {fields.map((f) => (
            <View key={f.key}>
              <BodyLabel>{f.label}</BodyLabel>
              <TextField
                value={(day.entries || {})[f.key] || ""}
                onChange={(e) => setEntry(dayIdx, f.key, e.target.value)}
                placeholder="Your answer…"
                fullWidth
                multiline
                minRows={1}
                variant="outlined"
              />
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

export function isTrackerComplete(v: TrackerResponse): boolean {
  if (!v.days) return false;
  // Complete once any single day has at least one filled entry.
  return v.days.some((d) =>
    Object.values(d.entries || {}).some((x) => (x || "").trim().length > 0)
  );
}

// ---------------------------------------------------------------------------
// Exercise 5 — Insight ranking: drag-and-drop ordering
// ---------------------------------------------------------------------------

export function RankingBody({
  exercise,
  value,
  onChange,
}: {
  exercise: Exercise;
  value: RankingResponse;
  onChange: (v: RankingResponse) => void;
}) {
  const options = React.useMemo(
    () => parseRankingOptions(exercise.prompt),
    [exercise.prompt]
  );

  // Resolve the displayed order from the saved ranking, then append any options
  // not yet in it (and drop saved entries no longer valid after a content edit),
  // DE-DUPING case-insensitively so a repeated saved value can't produce
  // duplicate @hello-pangea/dnd draggableIds. Keeps the list in sync with the
  // parsed options.
  const order = React.useMemo(() => {
    const valid = new Set(options.map((o) => o.toLowerCase()));
    const seen = new Set<string>();
    const saved: string[] = [];
    for (const r of value.ranking || []) {
      const k = r.toLowerCase();
      if (valid.has(k) && !seen.has(k)) {
        seen.add(k);
        saved.push(r);
      }
    }
    const missing = options.filter((o) => !seen.has(o.toLowerCase()));
    return [...saved, ...missing];
  }, [options, value.ranking]);

  // Seed the saved value with the displayed order on first mount when nothing is
  // saved yet, so a user who reviews the default order and Saves WITHOUT dragging
  // still gets a recorded ranking (and the exercise is marked complete). Only
  // fires when there are options and no saved ranking — never overwrites a real
  // saved order.
  React.useEffect(() => {
    if (options.length > 0 && (!value.ranking || value.ranking.length === 0)) {
      onChange({ ranking: order });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const next = order.slice();
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    onChange({ ranking: next });
  };

  if (options.length === 0) {
    return (
      <CustomText style={{ fontFamily: "Nunito", fontSize: 14, color: colors.gray[600] }}>
        No options to rank for this exercise.
      </CustomText>
    );
  }

  return (
    <View>
      <BodyLabel>Drag to rank — most important at the top.</BodyLabel>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="ranking">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {order.map((opt, idx) => (
                // `order` is de-duped, but index-prefix the draggableId so even
                // a stray repeat can't collide (a duplicate id breaks dnd).
                <Draggable key={`${idx}-${opt}`} draggableId={`${idx}-${opt}`} index={idx}>
                  {(prov, snapshot) => (
                    <div
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                      style={{
                        userSelect: "none",
                        padding: `${spacing.sm}px ${spacing.md}px`,
                        marginBottom: spacing.sm,
                        borderRadius: 10,
                        border: "2px solid #DCE4EB",
                        backgroundColor: snapshot.isDragging ? "#EEF2F6" : colors.white,
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.md,
                        fontFamily: "Nunito",
                        ...prov.draggableProps.style,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          color: colors.primary,
                          minWidth: 24,
                          textAlign: "center",
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span style={{ fontWeight: 600, color: colors.dark }}>{opt}</span>
                      <span style={{ marginLeft: "auto", color: colors.gray[400] }}>⠿</span>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </View>
  );
}

export function isRankingComplete(v: RankingResponse): boolean {
  // The user has engaged once a ranking is recorded.
  return !!v.ranking && v.ranking.length > 0;
}

// ---------------------------------------------------------------------------
// Small shared label
// ---------------------------------------------------------------------------
function BodyLabel({ children }: { children: React.ReactNode }) {
  return (
    <CustomText
      style={{
        fontFamily: "Nunito",
        fontSize: 14,
        fontWeight: 700,
        color: colors.gray[700],
        marginBottom: spacing.xs,
      }}
    >
      {children}
    </CustomText>
  );
}
